import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Trophy,
  Calendar,
  Clock,
  MapPin,
  Loader2,
  ChevronRight,
} from 'lucide-react';
import { useAthleteAuth } from '@/contexts/AthleteAuthContext';
import { api } from '@/hooks/useApi';

interface CompetitionItem {
  id: string;
  name: string;
  nameEn?: string;
  date: string;
  time: string;
  endTime?: string;
  venue: string;
  venueEn?: string;
  status: 'upcoming' | 'live' | 'completed' | 'notStarted';
  result?: string;
  rank?: number;
}

const STATUS_CONFIG: Record<
  string,
  { bg: string; text: string; label: string; labelEn: string }
> = {
  upcoming: { bg: 'bg-blue-100', text: 'text-blue-800', label: '即將開始', labelEn: 'Upcoming' },
  live: { bg: 'bg-green-100', text: 'text-green-800', label: '進行中', labelEn: 'Live' },
  completed: { bg: 'bg-gray-100', text: 'text-gray-800', label: '已結束', labelEn: 'Completed' },
  notStarted: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: '未開始', labelEn: 'Not Started' },
};

export default function AthleteCompetitionsPage() {
  const { token } = useAthleteAuth();
  const [competitions, setCompetitions] = useState<CompetitionItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;

    api
      .get('/me/competitions', {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const data = res.data?.data ?? res.data;
        setCompetitions(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        // Failed to fetch competitions
      })
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)]">我的競賽項目</h1>
        <p className="text-sm text-[var(--text-secondary)] mt-1">My Competitions</p>
      </div>

      {competitions.length === 0 ? (
        <div className="text-center py-20">
          <Trophy className="w-12 h-12 text-[var(--text-secondary)] mx-auto mb-4" />
          <h2 className="text-lg font-bold text-[var(--text-primary)]">尚未報名任何競賽項目</h2>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            No competitions registered yet.
          </p>
          <p className="text-xs text-[var(--text-secondary)] mt-2">
            報名由鄉區工作人員管理
            <br />
            Registration is managed by township staff.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {competitions.map((comp) => {
            const statusConfig = STATUS_CONFIG[comp.status] ?? STATUS_CONFIG.notStarted;

            return (
              <div
                key={comp.id}
                className="bg-[var(--bg-card)] rounded-xl border border-[var(--border)] p-5 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  {/* Left: Competition info */}
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[var(--primary)] text-white flex items-center justify-center shrink-0">
                      <Trophy className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-[var(--text-primary)]">
                        {comp.name}
                      </h3>
                      {comp.nameEn && (
                        <p className="text-xs text-[var(--text-secondary)]">{comp.nameEn}</p>
                      )}
                      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[var(--text-secondary)]">
                        <span className="flex items-center gap-1.5">
                          <Calendar className="w-4 h-4 shrink-0" />
                          {comp.date}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Clock className="w-4 h-4 shrink-0" />
                          {comp.time}
                          {comp.endTime && ` - ${comp.endTime}`}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <MapPin className="w-4 h-4 shrink-0" />
                          {comp.venue}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Right: Status badge */}
                  <div className="flex items-center gap-3 sm:flex-col sm:items-end">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${statusConfig.bg} ${statusConfig.text}`}
                    >
                      {statusConfig.label}
                      <span className="ml-1 font-normal opacity-70">/ {statusConfig.labelEn}</span>
                    </span>
                    {comp.status === 'completed' && comp.rank && (
                      <span className="text-sm font-bold text-[var(--primary)]">
                        第 {comp.rank} 名
                      </span>
                    )}
                    {comp.status === 'completed' && comp.result && (
                      <span className="text-xs text-[var(--text-secondary)]">{comp.result}</span>
                    )}
                  </div>
                </div>

                {/* Link to schedule */}
                <div className="mt-3 pt-3 border-t border-[var(--border)]">
                  <Link
                    to="/athlete/event"
                    className="inline-flex items-center gap-1 text-sm text-[var(--primary)] hover:underline"
                  >
                    查看詳細賽程 / View detailed schedule
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            );
          })}

          {/* Summary */}
          <div className="text-center text-sm text-[var(--text-secondary)] pt-4">
            共 {competitions.length} 個競賽項目 / {competitions.length} competition(s) total
          </div>
        </div>
      )}
    </div>
  );
}
